import { propertyPublicPagesApi } from '../../services/property-public-pages.api';
import type { PropertyPublicPagePublicDto } from '../../services/property-public-pages.api';
import { authService } from '../../services/auth';
import { resolveApiUrl } from '../../services/core.api';
import { onLocaleChange, t, translatePropertyStatus, translatePropertyType } from '../../services/i18n';
import { clearModalPortal, getModalPortalRoot, renderModalPortal } from '../shared/modal-portal';

type State = 'loading' | 'ready' | 'not-found' | 'error';

function esc(value: string): string {
  const span = document.createElement('span');
  span.textContent = value;
  return span.innerHTML;
}

function renderMultiline(value?: string | null): string {
  if (!value) return '';
  return esc(value).replace(/\n/g, '<br>');
}

export class RcPublicPropertyPage extends HTMLElement {
  private state: State = 'loading';
  private token: string | null = null;
  private property: PropertyPublicPagePublicDto | null = null;
  private activePhotoId: string | null = null;
  private applicationError = '';
  private applicationSuccess = false;
  private isApplying = false;
  private localeCleanup?: () => void;

  connectedCallback(): void {
    this.token = new URLSearchParams(window.location.search).get('token');
    this.localeCleanup = onLocaleChange(() => this.render());
    this.addEventListener('click', this.handleClick);
    getModalPortalRoot(this).addEventListener('click', this.handleClick);
    document.addEventListener('keydown', this.handleKeydown);
    this.render();
    void this.loadPage();
  }

  disconnectedCallback(): void {
    this.localeCleanup?.();
    this.removeEventListener('click', this.handleClick);
    getModalPortalRoot(this).removeEventListener('click', this.handleClick);
    document.removeEventListener('keydown', this.handleKeydown);
    clearModalPortal(this);
  }

  private async loadPage(): Promise<void> {
    if (!this.token) {
      this.state = 'not-found';
      this.render();
      return;
    }

    try {
      const property = await propertyPublicPagesApi.getByToken(this.token);
      if (!property) {
        this.state = 'not-found';
      } else {
        this.property = property;
        this.state = 'ready';
      }
    } catch {
      this.state = 'error';
    }

    this.render();
  }

  private render(): void {
    const activePhoto = this.property?.galleryPhotos.find((photo) => photo.id === this.activePhotoId) ?? null;
    const activePhotoIndex = activePhoto && this.property
      ? this.property.galleryPhotos.findIndex((photo) => photo.id === activePhoto.id)
      : -1;

    this.innerHTML = `
      <section class="public-property-shell">
        <div class="public-property-card">
          ${this.renderContent()}
        </div>
      </section>`;

    this.renderLightbox(activePhoto, activePhotoIndex);
  }

  private renderContent(): string {
    if (this.state === 'loading') {
      return `<p class="public-property-loading">${t('publicProperty.loading')}</p>`;
    }

    if (this.state === 'not-found') {
      return `
        <div class="public-property-empty">
          <h1>${t('publicProperty.notFoundTitle')}</h1>
          <p>${t('publicProperty.notFoundBody')}</p>
        </div>`;
    }

    if (this.state === 'error') {
      return `
        <div class="public-property-empty">
          <h1>${t('publicProperty.errorTitle')}</h1>
          <p>${t('publicProperty.errorBody')}</p>
        </div>`;
    }

    const property = this.property!;

    return `
      <div class="public-property-hero">
        <div class="public-property-hero-copy">
          <p class="public-property-eyebrow">${esc(t('publicProperty.hostedBy', { name: property.landlordDisplayName }))}</p>
          <h1>${esc(property.address)}</h1>
          <div class="public-property-badges">
            <span class="badge badge-info">${esc(translatePropertyType(property.type))}</span>
            <span class="badge badge-success">${esc(translatePropertyStatus(property.status))}</span>
          </div>
          ${property.description ? `<p class="public-property-intro">${renderMultiline(property.description)}</p>` : `<p class="public-property-intro">${t('publicProperty.noDescription')}</p>`}
        </div>
        <div class="public-property-hero-stats">
          <div class="public-property-stat">
            <span>${t('publicProperty.previewType')}</span>
            <strong>${esc(translatePropertyType(property.type))}</strong>
          </div>
          <div class="public-property-stat">
            <span>${t('publicProperty.previewStatus')}</span>
            <strong>${esc(translatePropertyStatus(property.status))}</strong>
          </div>
          <div class="public-property-stat">
            <span>${t('publicProperty.photosCount')}</span>
            <strong>${property.galleryPhotos.length}</strong>
          </div>
        </div>
      </div>

      <div class="public-property-content">
        <div class="public-property-section">
          <div class="public-property-section-header">
            <h2>${t('publicProperty.applyTitle')}</h2>
            <p>${t('publicProperty.applySubtitle')}</p>
          </div>
          ${this.renderApplicationPanel()}
        </div>
        <div class="public-property-section">
          <div class="public-property-section-header">
            <h2>${t('publicProperty.galleryTitle')}</h2>
            <p>${t('publicProperty.gallerySubtitle')}</p>
          </div>
          ${property.galleryPhotos.length
            ? `<div class="public-property-gallery-grid">
                ${property.galleryPhotos.map((photo) => `
                  <button
                    type="button"
                    class="public-property-gallery-thumb"
                    data-action="open-lightbox"
                    data-id="${photo.id}"
                    aria-label="${esc(t('publicProperty.openPreview', { fileName: photo.fileName }))}">
                    <img src="${resolveApiUrl(photo.downloadUrl)}" alt="${esc(t('publicProperty.galleryPhotoAlt', { fileName: photo.fileName }))}" loading="lazy">
                  </button>`).join('')}
              </div>`
            : `<div class="public-property-gallery-empty">${t('publicProperty.galleryEmpty')}</div>`}
        </div>
      </div>
    `;
  }

  private renderLightbox(
    activePhoto: PropertyPublicPagePublicDto['galleryPhotos'][number] | null,
    activePhotoIndex: number,
  ): void {
    const property = this.property;
    if (!activePhoto || !property) {
      renderModalPortal(this, '');
      return;
    }

    const canNavigatePrevious = activePhotoIndex > 0;
    const canNavigateNext = activePhotoIndex >= 0 && activePhotoIndex < property.galleryPhotos.length - 1;
    renderModalPortal(this, `
      <div class="modal-overlay public-property-lightbox" data-action="close-lightbox">
        <div class="modal public-property-lightbox-modal" role="dialog" aria-modal="true" aria-label="${esc(t('publicProperty.lightboxTitle'))}">
          <div class="public-property-lightbox-header">
            <div class="public-property-lightbox-copy">
              <strong>${esc(activePhoto.fileName)}</strong>
              <span>${esc(t('publicProperty.position', { current: String(activePhotoIndex + 1), total: String(property.galleryPhotos.length) }))}</span>
            </div>
            <button type="button" class="modal-close" data-action="close-lightbox" aria-label="${esc(t('publicProperty.closePreview'))}">
              &times;
            </button>
          </div>
          <div class="public-property-lightbox-stage">
            <button
              type="button"
              class="public-property-lightbox-nav"
              data-action="previous-photo"
              ${canNavigatePrevious ? '' : 'disabled'}
              aria-label="${esc(t('publicProperty.previous'))}">
              &#8249;
            </button>
            <figure class="public-property-lightbox-figure">
              <img src="${resolveApiUrl(activePhoto.downloadUrl)}" alt="${esc(t('publicProperty.galleryPhotoAlt', { fileName: activePhoto.fileName }))}">
            </figure>
            <button
              type="button"
              class="public-property-lightbox-nav"
              data-action="next-photo"
              ${canNavigateNext ? '' : 'disabled'}
              aria-label="${esc(t('publicProperty.next'))}">
              &#8250;
            </button>
          </div>
        </div>
      </div>
    `);
  }

  private renderApplicationPanel(): string {
    const currentUser = authService.currentUser;

    if (this.applicationSuccess) {
      return `<div class="empty-state">${t('publicProperty.applySuccess')}</div>`;
    }

    if (!currentUser) {
      return `
        <div class="empty-state">
          <p>${t('publicProperty.applyLoginRequired')}</p>
          <button type="button" class="btn btn-primary" data-action="start-application-login">${t('publicProperty.applyLoginButton')}</button>
        </div>
      `;
    }

    if (currentUser.accountMode === 'landlord') {
      return `
        <div class="empty-state">
          <p>${t('publicProperty.applyLandlordModeBlocked')}</p>
          <a href="/profile/" class="btn btn-outline">${t('publicProperty.applyGoToProfile')}</a>
        </div>
      `;
    }

    if (!currentUser.onboardingCompleted) {
      return `
        <div class="empty-state">
          <p>${t('publicProperty.applyOnboardingRequired')}</p>
          <button type="button" class="btn btn-primary" data-action="start-tenant-onboarding">${t('publicProperty.applyCompleteProfile')}</button>
        </div>
      `;
    }

    return `
      <div class="empty-state">
        <p>${t('publicProperty.applyConfirmPrompt')}</p>
        ${this.applicationError ? `<div class="form-error">${esc(this.applicationError)}</div>` : ''}
        <button type="button" class="btn btn-primary" data-action="confirm-application" ${this.isApplying ? 'disabled' : ''}>${this.isApplying ? t('common.saving') : t('publicProperty.applyConfirmButton')}</button>
      </div>
    `;
  }

  private handleClick = (event: Event): void => {
    const rawTarget = event.target as HTMLElement | null;
    const target = rawTarget?.closest<HTMLElement>('[data-action]');
    if (!target || !this.property) {
      return;
    }

    if (target.dataset.action === 'open-lightbox' && target.dataset.id) {
      this.activePhotoId = target.dataset.id;
      this.render();
      return;
    }

    if (target.dataset.action === 'close-lightbox') {
      if (target === rawTarget || target.classList.contains('modal-close')) {
        this.closeLightbox();
      }
      return;
    }

    if (target.dataset.action === 'previous-photo') {
      this.showRelativePhoto(-1);
      return;
    }

    if (target.dataset.action === 'next-photo') {
      this.showRelativePhoto(1);
      return;
    }

    if (target.dataset.action === 'start-application-login') {
      const redirectPath = `${window.location.pathname}${window.location.search}`;
      authService.rememberPostLoginRedirect(redirectPath);
      authService.rememberPostOnboardingRedirect(redirectPath);
      window.location.href = '/login/';
      return;
    }

    if (target.dataset.action === 'start-tenant-onboarding') {
      authService.rememberPostOnboardingRedirect(`${window.location.pathname}${window.location.search}`);
      window.location.href = '/onboarding/';
      return;
    }

    if (target.dataset.action === 'confirm-application') {
      void this.applyForProperty();
    }
  };

  private handleKeydown = (event: KeyboardEvent): void => {
    if (!this.activePhotoId) {
      return;
    }

    if (event.key === 'Escape') {
      this.closeLightbox();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.showRelativePhoto(-1);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.showRelativePhoto(1);
    }
  };

  private closeLightbox(): void {
    if (!this.activePhotoId) {
      return;
    }

    this.activePhotoId = null;
    this.render();
  }

  private showRelativePhoto(offset: number): void {
    if (!this.activePhotoId || !this.property) {
      return;
    }

    const currentIndex = this.property.galleryPhotos.findIndex((photo) => photo.id === this.activePhotoId);
    if (currentIndex < 0) {
      this.closeLightbox();
      return;
    }

    const nextIndex = currentIndex + offset;
    if (nextIndex < 0 || nextIndex >= this.property.galleryPhotos.length) {
      return;
    }

    this.activePhotoId = this.property.galleryPhotos[nextIndex].id;
    this.render();
  }

  private async applyForProperty(): Promise<void> {
    if (!this.token || this.isApplying) {
      return;
    }

    if (!window.confirm(t('publicProperty.applyBrowserConfirm'))) {
      return;
    }

    this.isApplying = true;
    this.applicationError = '';
    this.render();

    try {
      await propertyPublicPagesApi.apply(this.token);
      this.applicationSuccess = true;
    } catch (error: any) {
      this.applicationError = error?.message || t('publicProperty.applyError');
    } finally {
      this.isApplying = false;
      this.render();
    }
  }
}

if (!customElements.get('rc-public-property-page')) {
  customElements.define('rc-public-property-page', RcPublicPropertyPage);
}
